import { getDatabase, increment, push, ref, update } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { formatDateForStorage } from '../Helpers/DateUtils';
import type { ActivityAction, ActivityDetails } from '../types/activity';

/**
 * Användaraktivitet: logs one user action to `activity/{date}/{uid}/{entryKey}`
 * (superadmin-only read; schema in the workspace CLAUDE.md).
 *
 * Call it once per user action (a save, a delete, an upload, a completion),
 * after the write succeeded, never per DB path. A repeat of the same action on
 * the same target in the same building within 15 minutes, on the same day,
 * updates the last entry (`when`, `count`) instead of adding one. `session.started` is written only when nothing was
 * logged for the user in the last 15 minutes, so a reload mid-visit is the
 * same visit. The last entry is kept in localStorage to survive reloads.
 *
 * Fire-and-forget: it never throws and never blocks the caller.
 */

export const COLLAPSE_WINDOW_MS = 15 * 60 * 1000;

const STORAGE_KEY = 'activityRecorder.lastEntry';

interface LastEntry {
    uid: string;
    path: string;
    action: ActivityAction;
    targetKey: string | null;
    buildingKey: string | null;
    at: number;
    /** Last time anything was logged for `uid`; a session within the window is the same visit. */
    lastActivityAt: number;
}

let lastEntry: LastEntry | null | undefined;

function loadLastEntry(): LastEntry | null {
    if (lastEntry !== undefined) return lastEntry;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) as Partial<LastEntry> : null;
        lastEntry = parsed && typeof parsed.uid === 'string' && typeof parsed.path === 'string'
            && typeof parsed.at === 'number' && typeof parsed.lastActivityAt === 'number'
            ? parsed as LastEntry
            : null;
    } catch {
        lastEntry = null;
    }
    return lastEntry;
}

function saveLastEntry(entry: LastEntry): void {
    lastEntry = entry;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
    } catch {
        // Private mode or blocked storage: collapse still works within this page load.
    }
}

/** Test hook: forget the last entry so the next call pushes. */
export function resetActivityRecorder(): void {
    lastEntry = null;
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        // ignore
    }
}

function cleanDetails(details: ActivityDetails): ActivityDetails {
    const out: ActivityDetails = {};
    if (details.buildingKey) out.buildingKey = details.buildingKey;
    if (details.targetKey) out.targetKey = details.targetKey;
    if (details.label) out.label = details.label.slice(0, 200);
    return out;
}

export function recordActivity(action: ActivityAction, details: ActivityDetails = {}): void {
    void writeActivity(action, details).catch((error) => {
        console.warn('Kunde inte logga aktivitet:', error);
    });
}

/** Awaitable variant of `recordActivity`, for tests. */
export async function writeActivity(action: ActivityAction, details: ActivityDetails = {}, now: Date = new Date()): Promise<void> {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    const db = getDatabase();
    const when = formatDateForStorage(now);
    const clean = cleanDetails(details);
    const targetKey = clean.targetKey ?? null;
    const buildingKey = clean.buildingKey ?? null;
    const dateKey = when.slice(0, 10);
    const at = now.getTime();
    const withinWindow = (t: number) => at - t >= 0 && at - t <= COLLAPSE_WINDOW_MS;

    const last = loadLastEntry();
    if (last && last.uid === uid) {
        if (action === 'session.started' && withinWindow(last.lastActivityAt)) {
            saveLastEntry({ ...last, lastActivityAt: at });
            return;
        }
        // Same day only: an entry never moves to the next date key.
        if (last.action === action && last.targetKey === targetKey && (last.buildingKey ?? null) === buildingKey
            && last.path.startsWith(`activity/${dateKey}/`) && withinWindow(last.at)) {
            await update(ref(db, last.path), {
                when,
                count: increment(1),
                ...(clean.label ? { label: clean.label } : {}),
            });
            saveLastEntry({ ...last, at, lastActivityAt: at });
            return;
        }
    }

    const dayRef = ref(db, `activity/${dateKey}/${uid}`);
    const entryRef = push(dayRef);
    const path = `activity/${dateKey}/${uid}/${entryRef.key}`;
    await update(ref(db, path), { when, platform: 'web', action, count: 1, ...clean });
    // Remembered only once written, so a rejected entry is never collapsed into.
    saveLastEntry({ uid, path, action, targetKey, buildingKey, at, lastActivityAt: at });
}
